/*
scrollableDropdown.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { useRef, useState, useEffect, useCallback, useMemo, memo } from 'react'
import { scrollerClickEvent } from './scrollWrapper'
import ScrollWrapperLoader from './scrollWrapperLoader'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { is5inches } from '../modules/util'
export interface DropdownOption {
  name: string
  translationKey: string
  value: string | number
  glyph?: string
  imgPath?: string
}

interface DropdownAction {
  displayName: string
  onClick: () => void
}

interface FixedPositioning {
  left: number
  top: number
}

interface ScrollableDropdownProps {
  id: string
  title: string
  options: DropdownOption[]
  selectedOption?: DropdownOption
  defaultOption: DropdownOption
  onSelect: (option: DropdownOption) => void
  bottomAction?: DropdownAction
  selectionOutOfBoundAction?: () => void
  style?: 'none' | 'button'
  caret?: 'top' | 'down' | 'left' | 'right'
  caretPosition?: 'left' | 'right'
  fixedPositioning?: FixedPositioning
  fixedHeight?: number
  displayArrow?: boolean
  displayTitleInDropdown?: boolean
  justifyLabelsLeft?: boolean
  isOpen?: boolean
  onToggle?: (isOpen: boolean) => void 
  clickPos?: { x: number; y: number }
}

function ScrollableDropdown (props: ScrollableDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const {
    id,
    options,
    selectedOption,
    title,
    onSelect,
    bottomAction,
    defaultOption,
    selectionOutOfBoundAction,
    style,
    fixedPositioning,
    fixedHeight,
    displayArrow = true,
    displayTitleInDropdown = true,
    justifyLabelsLeft = false,
    isOpen,
    onToggle
  } = props
  const menuId = `${id}-menu`
  const { t } = useTranslation()
  const initialSelection = useMemo(() => selectedOption || defaultOption, [defaultOption, selectedOption])
  const hasGlyphOptions = useMemo(
    () => options.some(option => Boolean(option.glyph)),
    [options]
  )
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 }) // Track click position
  const [arrowPosition, setArrowPosition] = useState({ top: 0, side: 'right' })
  const isControlled = isOpen !== undefined;
  const [show, setShow] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const lastSelectionRef = useRef<number>(0);
  const suppressNextClickRef = useRef(false);
  //const show = isControlled ? isOpen : internalShow;

  const getScrollHeight = useCallback(() => {
    const minHeight = is5inches
      ? options.length <= 2
        ? 64 * options.length
        : 192
      : options.length <= 2
      ? 60 * options.length
      : 180
    const maxHeight = is5inches
      ? displayTitleInDropdown
        ? 384
        : 438
      : displayTitleInDropdown
      ? 496
      : 550
    let height = options.length * 33
    if (bottomAction != undefined) {
      height = height + 33
    }
    height = Math.max(height, minHeight)
    height = Math.min(height, maxHeight);
    height = fixedHeight ? fixedHeight : height
    return height
  }, [bottomAction, displayTitleInDropdown, fixedHeight, options.length])

  const setClickPositionFromScrollEvent = useCallback((e: CustomEvent)=>{
    if(e.detail.scrollId){
      setClickPosition({x: e.detail.x, y: e.detail.y})
    }
  },[])

  useEffect(()=>{
    window.addEventListener(scrollerClickEvent, setClickPositionFromScrollEvent as EventListener)
    return (()=>{
      window.removeEventListener(scrollerClickEvent, setClickPositionFromScrollEvent as EventListener)
    })
  },[])
  useEffect(() => {
    const handleClickCapture = (event: MouseEvent) => {
      if (!suppressNextClickRef.current) {
        return
      }
      suppressNextClickRef.current = false
      event.preventDefault()
      event.stopPropagation()
    }
    document.addEventListener('click', handleClickCapture, true)
    return () => document.removeEventListener('click', handleClickCapture, true)
  }, [])

  const onDropdownToggle = useCallback(
    () => {
      //event.stopPropagation();
  
      const newShowState = !show;
      if (isControlled) {
        onToggle?.(newShowState);
      } else {
        setShow(newShowState);
      }
    },
    [isControlled, onToggle, show]
  );

  const closeDropdown = useCallback(() => {
    if (isControlled) {
      onToggle?.(false);
    } else {
      setShow(false);
    }
  },[isControlled, onToggle])
  const handleOptionSelect = useCallback(
    (option: DropdownOption) => {
      const now = Date.now()
      if (now - lastSelectionRef.current < 250) {
        return
      }
      lastSelectionRef.current = now
      onSelect(option)
      closeDropdown()
    },
    [closeDropdown, onSelect]
  )
  const handleOptionSelectFromPointer = useCallback(
    (option: DropdownOption) => {
      suppressNextClickRef.current = true
      handleOptionSelect(option)
    },
    [handleOptionSelect]
  )
  const handleBottomAction = useCallback(() => {
    if (!bottomAction) {
      return
    }
    const now = Date.now()
    if (now - lastSelectionRef.current < 250) {
      return
    }
    lastSelectionRef.current = now
    bottomAction.onClick()
    closeDropdown()
  }, [bottomAction, closeDropdown])
  const handleBottomActionFromPointer = useCallback(() => {
    suppressNextClickRef.current = true
    handleBottomAction()
  }, [handleBottomAction])
  const updatePosition = useCallback(() => {
    const parentRect = dropdownRef.current!.getBoundingClientRect()
    const menuWidth = menuRef.current?.offsetWidth || 0
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const menuHeight = getScrollHeight() + 54

    // Calculate horizontal position based on click position
    let left = clickPosition.x - menuWidth // Position the right edge at the click position
    let arrowSide: 'left' | 'right' = 'right';

    // if (left < 0) {
    //   left = 0 // Ensure it doesn't go off the left edge of the viewport
    // } else if (left + menuWidth > viewportWidth) {
    //   left = viewportWidth - menuWidth // Ensure it doesn't go off the right edge
    // }
    // Check if we're too close to the left edge
  if (left < 0) {
    left = clickPosition.x; // Position menu to the right of click
    arrowSide = 'left'; // Flip arrow to left side
  } 
  // Check if we're too close to the right edge
  else if (left + menuWidth > viewportWidth) {
    left = clickPosition.x - menuWidth; // Position menu to the left of click
    arrowSide = 'right'; // Keep arrow on right side
  }

  // Ensure we don't go offscreen after adjustment
  left = Math.max(0, Math.min(left, viewportWidth - menuWidth));

    // Calculate vertical position with containment
    let top = parentRect.top
    const minTop = menuHeight / 2
    const maxTop = viewportHeight - menuHeight / 2
    const idealTop = top - menuHeight / 2
    const actualTop = Math.max(
      0,
      Math.min(idealTop, viewportHeight - menuHeight)
    )
    // Adjust for viewport boundaries
    if (top + menuHeight / 2 > viewportHeight) {
      top = viewportHeight - menuHeight / 2
    }
    if (top - menuHeight / 2 < 0) {
      top = menuHeight / 2
    }
    // Calculate arrow position relative to dropdown
    const parentCenterY = parentRect.top + parentRect.height / 2
    const arrowTop = is5inches
      ? parentCenterY - actualTop
      : parentCenterY - actualTop
    setMenuPosition({
      top: fixedPositioning
        ? fixedPositioning.top
        : Math.max(minTop, Math.min(top, maxTop)),
      left: fixedPositioning ? fixedPositioning.left : left
    })
    setArrowPosition({ top: arrowTop, side: arrowSide })
  },[clickPosition.x, fixedPositioning, getScrollHeight])
  // Position calculation effect
  useEffect(() => {
    if (show && dropdownRef.current) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      return () => window.removeEventListener('resize', updatePosition)
    }
  }, [bottomAction, dropdownWidth, getScrollHeight, options.length, show, updatePosition])

  const handleClickOutside = useCallback((event: MouseEvent | TouchEvent) => {
    const menuElement = document.getElementById(menuId)
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      menuElement &&
      !menuElement.contains(event.target as Node)
    ) {
      closeDropdown()
    }
  },[closeDropdown, menuId])
  // Click-away detection effect
  useEffect(() => {
    if (show) {
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside);
      }
    }
  }, [show, menuId, handleClickOutside])

  useEffect(() => {
    if (isControlled) {
      setShow(isOpen ?? false);
    }
  }, [isOpen, isControlled]);

  useEffect(() => {
    if (!show || !menuRef.current) {
      return
    }
    const viewportPadding = 24
    const maxAllowedWidth = Math.max(0, window.innerWidth - viewportPadding)
    const horizontalGutter = 24 // 12px left + 12px right from scrollableDropdown.scss
    const menuElement = menuRef.current
    const previousInlineWidth = menuElement.style.width
    menuElement.style.width = 'auto'

    let maxItemContentWidth = 0
    const dropdownItems = menuElement.querySelectorAll<HTMLElement>(
      '.dropdown-item .content-container'
    )
    dropdownItems.forEach(item => {
      maxItemContentWidth = Math.max(maxItemContentWidth, Math.ceil(item.scrollWidth))
    })
    let maxRequiredWidth = maxItemContentWidth + horizontalGutter

    const titleHeaderElement = menuElement.querySelector<HTMLElement>(
      '.dropdown-title'
    )
    const titleElement = menuElement.querySelector<HTMLElement>(
      '.dropdown-title span'
    )
    if (titleElement && titleHeaderElement) {
      const previousTitleWhiteSpace = titleElement.style.whiteSpace
      const previousTitleDisplay = titleElement.style.display
      titleElement.style.whiteSpace = 'nowrap'
      titleElement.style.display = 'inline-block'

      const titleHeaderStyles = window.getComputedStyle(titleHeaderElement)
      const titleHeaderHorizontalPadding =
        parseFloat(titleHeaderStyles.paddingLeft || '0') +
        parseFloat(titleHeaderStyles.paddingRight || '0')
      const titleRequiredWidth =
        Math.ceil(titleElement.scrollWidth) + Math.ceil(titleHeaderHorizontalPadding)

      maxRequiredWidth = Math.max(maxRequiredWidth, titleRequiredWidth)
      titleElement.style.whiteSpace = previousTitleWhiteSpace
      titleElement.style.display = previousTitleDisplay
    }
    const calculatedWidth = Math.min(
      maxRequiredWidth,
      maxAllowedWidth
    )
    menuElement.style.width = previousInlineWidth
    setDropdownWidth(`${calculatedWidth}px`)
  }, [bottomAction, displayTitleInDropdown, options, show, title])

  return useMemo(()=>  (
    <>
      {options.find(c => c.value == initialSelection.value) ? (
        <div
          ref={dropdownRef}
          className='xrx-dropdown dropleft feedback-label'
          style={{ position: 'relative' }}
        >
          {style && style == 'button' ? (
            <>
              {title && <span>{t(title)}</span>}
              <button
                className='scrollable-dropdown-input-btn xrx-btn dropdown-toggle xrx-input row mx-0'
                type='button'
                id={id}
                onClick={onDropdownToggle}
                //onTouchStart={onDropdownTouchToggle}
                aria-haspopup='true'
                aria-expanded='false'
              >
                <span className='btn-label col-6 pr-0'>
                  {t(initialSelection.translationKey)}
                </span>
                <i className='xgl-sort_down xglsize-32 caret'></i>
              </button>
            </>
          ) : (
            <span
              aria-haspopup='true'
              onClick={onDropdownToggle}
              //onTouchStart={onDropdownTouchToggle}
              id={id}
              aria-expanded={show}
              style={{ cursor: 'pointer' }}
            >
              {
                initialSelection.glyph && (<i className={`${`${initialSelection.glyph}`} xglsize-32 inline-icon pr-2`}></i>)
              }{t(initialSelection.translationKey)}
            </span>
          )}

          {createPortal(
            <div
              ref={menuRef}
              role='menu'
              className={`dropdown-menu scrollable-dropdown-menu ${show ? 'show' : ''}`}
              aria-labelledby={id}
              id={menuId}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                margin: 0,
                zIndex: 1000,
                width: dropdownWidth
              }}
            >
              {displayArrow && (
                <div
                  className={`dropdown-arrow ${arrowPosition.side}`}
                  style={{
                    position: 'absolute',
                    ...(arrowPosition.side === 'right' ?  {right:'-8px'} : {left:'-8px'}), // Position arrow at the right edge
                    top: `${arrowPosition.top}px`, // Position arrow vertically based on click
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    borderWidth: '8px 0 8px 8px',
                    borderColor: 'transparent transparent transparent #fff', // Match dropdown background
                    transform: arrowPosition.side === 'left' ? 'rotate(180deg)' : 'none'
                  }}
                />
              )}
              {displayTitleInDropdown && (
                <div className='list-group-header font-weight-bold dropdown-title'>
                  <span>{t(title)}</span>
                </div>
              )}
              <div
                className={`xrx-list-group list-group-flush ${
                  displayTitleInDropdown ? '' : 'no-title'
                }`}
                style={{
                  height: getScrollHeight()
                }}
              >
                <ScrollWrapperLoader>
                  <div className='scrollable-dropdown'>
                    <div>
                      {options.map(option => (
                        <a
                          key={option.value}
                          onClick={() => handleOptionSelect(option)}
                          onPointerUp={() => handleOptionSelectFromPointer(option)}
                          onTouchEnd={() => handleOptionSelectFromPointer(option)}
                          className={`dropdown-item list-group-item ${
                            !bottomAction &&
                            options.indexOf(option) === options.length - 1
                              ? ''
                              : 'scrollable-divider'
                          } list-group-item-action ${
                            selectedOption?.value === option.value
                              ? 'active'
                              : ''
                          }`}
                        >
                          <div
                            className={`content-container ${
                              option.imgPath
                                ? 'justify-content-left'
                                : justifyLabelsLeft
                                ? 'justify-label-left'
                                : ''
                            }`}
                          >
                            {option.imgPath && (
                              <div className='left-object custom-icon-rounded'>
                                <img
                                  src={option.imgPath}
                                  className='img-fluid ml-0'
                                  alt={option.name}
                                />
                              </div>
                            )}
                            {option.glyph && (
                              <div className='left-object px-4'>
                                <i className={`${option.glyph} xglsize-32 mx-0`}></i>
                              </div>
                            )}
                            {!option.glyph && !option.imgPath && hasGlyphOptions && (
                              <div className='left-object px-4' aria-hidden='true'>
                                <i className='xgl-long_edge_feed xglsize-32 mx-0 glyph-placeholder-icon'></i>
                              </div>
                            )}
                            <div
                              className={`left-label ${
                                justifyLabelsLeft ? 'pl-4' : ''
                              }`}
                              {...(option.glyph
                                ? { style: { width: '100%' } }
                                : {})}
                            >
                              <span>{t(option.translationKey)}</span>
                            </div>
                          </div>
                        </a>
                      ))}
                      {bottomAction && (
                        <a
                          key={`${menuId}-bottom-action`}
                          onClick={handleBottomAction}
                          onPointerUp={handleBottomActionFromPointer}
                          onTouchEnd={handleBottomActionFromPointer}
                          className='dropdown-item list-group-item scrollable-divider list-group-item-action'
                        >
                          <div
                            className={`content-container ${
                              justifyLabelsLeft ? 'justify-label-left' : ''
                            }`}
                          >
                            {hasGlyphOptions && (
                              <div className='left-object px-4' aria-hidden='true'>
                                <i className='xgl-long_edge_feed xglsize-32 mx-0 glyph-placeholder-icon'></i>
                              </div>
                            )}
                            <div
                              className={`left-label ${
                                justifyLabelsLeft ? 'pl-4' : ''
                              }`}
                            >
                              <span>{bottomAction.displayName}</span>
                            </div>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                </ScrollWrapperLoader>
              </div>
            </div>,
            document.body
          )}
        </div>
      ) : (
        <div className='right-object' onClick={selectionOutOfBoundAction}>
          <div className='feedback-label'>
            <span>
              {t(
                selectedOption?.translationKey ?? defaultOption.translationKey
              )}
            </span>
          </div>
        </div>
      )}
    </>
  ),[arrowPosition.side, arrowPosition.top, bottomAction, closeDropdown, defaultOption.translationKey, displayArrow, displayTitleInDropdown, getScrollHeight, hasGlyphOptions, id, initialSelection.glyph, initialSelection.translationKey, initialSelection.value, justifyLabelsLeft, menuId, menuPosition.left, menuPosition.top, onDropdownToggle, onSelect, options, selectedOption?.translationKey, selectedOption?.value, selectionOutOfBoundAction, show, style, t, title])
}

export default memo(ScrollableDropdown)
